"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { Customer, Vehicle, Subscription } from "@/types";
import { Subscription as SubscriptionPlanType } from "@/types/subscription.types";
import { PaymentMode } from "@/types/enums.types";
import { SubscriptionFeature } from "@/types/userSubscription.types";
import { formatCurrency, generateTimeSlots } from "@/lib/utils";

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Generate time slots with 15-minute intervals
const timeSlots = generateTimeSlots();

// 8-wash plans that require 2 scheduling rules (2 appointments/week × 4 weeks)
const TWO_RULE_PLANS = ["PLATINUM", "GOLD", "SILVER LITE"];

export interface AddSubscriptionFormData {
  customerId: string;
  subscriptionId: string;
  vehicleId1?: string;
  vehicleId2?: string;
  recurringDay: string;
  preferredTime: string;
  recurringDay2?: string;
  preferredTime2?: string;
  paymentMode: PaymentMode;
  discount?: number;
}

interface AddSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: Customer[];
  vehicles: Vehicle[];
  subscriptionPlans: SubscriptionPlanType[];
  onCreate: (
    data: AddSubscriptionFormData,
    planFeatures?: SubscriptionFeature[],
  ) => Promise<void>;
}

export function AddSubscriptionDialog({
  open,
  onOpenChange,
  customers,
  vehicles,
  subscriptionPlans,
  onCreate,
}: AddSubscriptionDialogProps) {
  const [formData, setFormData] = useState<AddSubscriptionFormData>({
    customerId: "",
    subscriptionId: "",
    vehicleId1: "",
    vehicleId2: "",
    recurringDay: "Monday",
    preferredTime: "10:00 AM",
    recurringDay2: "Wednesday",
    preferredTime2: "10:00 AM",
    paymentMode: "CASH",
    discount: 0,
  });

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");

  // Filter customers by search term
  const filteredCustomers = useMemo(() => {
    if (!customerSearchTerm) return customers;
    const search = customerSearchTerm.toLowerCase();
    return customers.filter(
      (c) =>
        c.name.toLowerCase().includes(search) ||
        c.phone.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search),
    );
  }, [customers, customerSearchTerm]);

  // Get vehicles for selected customer
  const customerVehicles = useMemo(() => {
    if (!formData.customerId) return [];
    return vehicles.filter((v) => v.customerId === formData.customerId);
  }, [vehicles, formData.customerId]);

  // Get selected subscription plan
  const selectedPlan = useMemo(() => {
    return subscriptionPlans.find((p) => p.id === formData.subscriptionId);
  }, [subscriptionPlans, formData.subscriptionId]);

  // Check if selected plan requires 2 scheduling rules
  const isTwoRulePlan = useMemo(() => {
    if (!selectedPlan) return false;
    const planName = selectedPlan.name
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
    return TWO_RULE_PLANS.includes(planName);
  }, [selectedPlan]);

  // Auto-select vehicle if customer has only one
  useEffect(() => {
    if (customerVehicles.length === 1) {
      setFormData((prev) => ({
        ...prev,
        vehicleId1: prev.vehicleId1 || customerVehicles[0].id,
        vehicleId2: prev.vehicleId2 || customerVehicles[0].id,
      }));
    } else if (customerVehicles.length === 0) {
      setFormData((prev) => ({
        ...prev,
        vehicleId1: "",
        vehicleId2: "",
      }));
    }
  }, [customerVehicles]);

  const updateField = <K extends keyof AddSubscriptionFormData>(
    field: K,
    value: AddSubscriptionFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!formData.customerId) {
      return;
    }

    if (!formData.subscriptionId) {
      return;
    }

    // Verify we have the correct subscription ID (not name)
    if (!selectedPlan) {
      console.error("Selected plan not found for ID:", formData.subscriptionId);
      return;
    }

    // Validate: 2-rule plans must have Schedule 2 filled
    if (
      isTwoRulePlan &&
      (!formData.recurringDay2 || !formData.preferredTime2)
    ) {
      return;
    }

    // Ensure we're passing the ID, not the name
    const formDataWithId = {
      ...formData,
      subscriptionId: selectedPlan.id, // Explicitly use the plan's ID
      // Only include Schedule 2 if this is a 2-rule plan
      recurringDay2: isTwoRulePlan ? formData.recurringDay2 : undefined,
      preferredTime2: isTwoRulePlan ? formData.preferredTime2 : undefined,
      vehicleId2: isTwoRulePlan ? formData.vehicleId2 : undefined,
    };

    // Pass the selected plan's features
    const selectedPlanFeatures = selectedPlan?.features || [];
    await onCreate(formDataWithId, selectedPlanFeatures);
    // Reset form
    setFormData({
      customerId: "",
      subscriptionId: "",
      vehicleId1: "",
      vehicleId2: "",
      recurringDay: "Monday",
      preferredTime: "10:00 AM",
      recurringDay2: "Wednesday",
      preferredTime2: "10:00 AM",
      paymentMode: "CASH",
      discount: 0,
    });
    setCustomerSearchTerm("");
  };

  const handleCancel = () => {
    setFormData({
      customerId: "",
      subscriptionId: "",
      vehicleId1: "",
      vehicleId2: "",
      recurringDay: "Monday",
      preferredTime: "10:00 AM",
      recurringDay2: "Wednesday",
      preferredTime2: "10:00 AM",
      paymentMode: "CASH",
      discount: 0,
    });
    setCustomerSearchTerm("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Subscription for Customer</DialogTitle>
          <DialogDescription>
            Assign a subscription plan to a customer. Fields marked with * are
            required.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Customer Selection */}
          <div className="grid gap-2">
            <Label htmlFor="customer">Customer *</Label>
            <Select
              value={formData.customerId}
              onValueChange={(value) => updateField("customerId", value)}
            >
              <SelectTrigger id="customer">
                <SelectValue placeholder="Select customer" />
              </SelectTrigger>
              <SelectContent>
                <div
                  className="sticky top-0 z-10 bg-popover border-b px-1 py-2"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or phone..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                </div>
                {filteredCustomers.length > 0 ? (
                  filteredCustomers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name} - {customer.phone}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No customers found
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Subscription Plan Selection */}
          <div className="grid gap-2">
            <Label htmlFor="subscriptionPlan">Subscription Plan *</Label>
            <Select
              value={formData.subscriptionId}
              onValueChange={(value) => {
                updateField("subscriptionId", value);
              }}
            >
              <SelectTrigger id="subscriptionPlan">
                <SelectValue placeholder="Select subscription plan" />
              </SelectTrigger>
              <SelectContent>
                {subscriptionPlans
                  .filter((p) => !p.isDeleted)
                  .map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.name} - {formatCurrency(plan.price)} /{" "}
                      {plan.duration}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {selectedPlan && (
              <div className="text-sm text-muted-foreground mt-1">
                Price: {formatCurrency(selectedPlan.price)} | Duration:{" "}
                {selectedPlan.duration}
                {isTwoRulePlan
                  ? " | 8 washes (2 per week)"
                  : " | 4 washes (1 per week)"}
              </div>
            )}
          </div>

          {/* Vehicle Selection — removed from here, now placed per-schedule below */}

          {/* Schedule 1 */}
          <div className="rounded-lg border p-4 space-y-4">
            <div className="text-sm font-medium text-muted-foreground">
              {isTwoRulePlan ? "Schedule 1" : "Schedule"}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="recurringDay">
                  {isTwoRulePlan
                    ? "Day *"
                    : "Recurring Day (Optional)"}
                </Label>
                <Select
                  value={formData.recurringDay}
                  onValueChange={(value) => updateField("recurringDay", value)}
                >
                  <SelectTrigger id="recurringDay">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weekDays.map((day) => (
                      <SelectItem key={day} value={day}>
                        {day}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="preferredTime">
                  {isTwoRulePlan
                    ? "Time *"
                    : "Preferred Time (Optional)"}
                </Label>
                <Select
                  value={formData.preferredTime}
                  onValueChange={(value) => updateField("preferredTime", value)}
                >
                  <SelectTrigger id="preferredTime">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot.value} value={slot.label}>
                        {slot.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vehicle for Schedule 1 */}
            <div className="grid gap-2">
              <Label htmlFor="vehicle1">Vehicle (Optional)</Label>
              <Select
                value={formData.vehicleId1 || ""}
                onValueChange={(value) =>
                  updateField("vehicleId1", value || undefined)
                }
                disabled={!formData.customerId || customerVehicles.length === 0}
              >
                <SelectTrigger id="vehicle1">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  {customerVehicles.length > 0 ? (
                    customerVehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>
                        {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                      {formData.customerId
                        ? "No vehicles found for this customer"
                        : "Select a customer first"}
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Schedule 2 — Only for 2-rule plans (Platinum, Gold, Silver Lite) */}
          {isTwoRulePlan && (
            <div className="rounded-lg border p-4 space-y-4">
              <div className="text-sm font-medium text-muted-foreground">
                Schedule 2
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="recurringDay2">Day *</Label>
                  <Select
                    value={formData.recurringDay2 || "Wednesday"}
                    onValueChange={(value) => updateField("recurringDay2", value)}
                  >
                    <SelectTrigger id="recurringDay2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekDays.map((day) => (
                        <SelectItem key={day} value={day}>
                          {day}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="preferredTime2">Time *</Label>
                  <Select
                    value={formData.preferredTime2 || "10:00 AM"}
                    onValueChange={(value) =>
                      updateField("preferredTime2", value)
                    }
                  >
                    <SelectTrigger id="preferredTime2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot.value} value={slot.label}>
                          {slot.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Vehicle for Schedule 2 */}
              <div className="grid gap-2">
                <Label htmlFor="vehicle2">Vehicle (Optional)</Label>
                <Select
                  value={formData.vehicleId2 || ""}
                  onValueChange={(value) =>
                    updateField("vehicleId2", value || undefined)
                  }
                  disabled={!formData.customerId || customerVehicles.length === 0}
                >
                  <SelectTrigger id="vehicle2">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {customerVehicles.length > 0 ? (
                      customerVehicles.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.make} {vehicle.model} - {vehicle.plateNumber}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                        {formData.customerId
                          ? "No vehicles found for this customer"
                          : "Select a customer first"}
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Payment Mode */}
          <div className="grid gap-2">
            <Label htmlFor="paymentMode">Payment Mode (Optional)</Label>
            <Select
              value={formData.paymentMode}
              onValueChange={(value) =>
                updateField("paymentMode", value as PaymentMode)
              }
            >
              <SelectTrigger id="paymentMode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CASH">Cash</SelectItem>
                <SelectItem value="CARD">Card</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Discount */}
          <div className="grid gap-2">
            <Label htmlFor="discount">Discount (Optional)</Label>
            <Input
              id="discount"
              type="number"
              min={0}
              value={formData.discount || 0}
              onChange={(e) =>
                updateField("discount", Number(e.target.value) || 0)
              }
              placeholder="Enter discount amount"
            />
            {selectedPlan && formData.discount && formData.discount > 0 && (
              <p className="text-sm text-muted-foreground">
                Final amount:{" "}
                {formatCurrency(selectedPlan.price - formData.discount)}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!formData.customerId || !formData.subscriptionId}
          >
            Create Subscription
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
